// Eversill: make the custom offer form add to cart without leaving the page,
// so the theme's side cart drawer opens instead of the /cart page.
//
// This mirrors what Horizon's own product-form.js does. If the import below
// fails for any reason, this module never runs and the form falls back to a
// normal POST to /cart, which still works. A sale is never lost.

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// Horizon declares `Theme` as a global const, so it is NOT on window.
// Read it through the global binding first, window as a fallback.
function theme() {
  try {
    if (typeof Theme !== 'undefined') return Theme;
  } catch (e) {}
  return window.Theme;
}

function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');

  if (cartItems) {
    return customElements.whenDefined('cart-items-component').then(() => cartItems.fetchCartData());
  }

  const cartUrl = (theme() && theme().routes && theme().routes.cart_url) || '/cart';

  return fetch(`${cartUrl}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

function wire(form) {
  if (!form || form.dataset.evDrawer === '1') return;
  form.dataset.evDrawer = '1';

  form.addEventListener('submit', (event) => {
    // No theme routes means no AJAX cart. Let the browser post normally.
    const T = theme();
    if (!T || !T.routes || !T.routes.cart_add_url) return;

    event.preventDefault();

    const button = form.querySelector('#ev-add');
    if (button && button.disabled) return;
    if (button) button.disabled = true;

    const formData = new FormData(form);
    const ids = sectionIds();
    if (ids.length) formData.append('sections', ids.join(','));

    const itemCount = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: itemCount }],
        promise: deferred.promise,
      })
    );

    fetch(T.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    })
      .then((response) => response.json())
      .then((result) => {
        const didError = Boolean(result && result.status);

        if (didError) {
          form.dispatchEvent(
            new CartErrorEvent({
              error: result.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: result.description, errors: result.errors },
            })
          );
        }

        return refreshCart().then((cart) => {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError,
              items: cart.items,
              source: 'eversill-buybox',
              itemCount,
              sections: didError ? undefined : result.sections,
            },
          });
        });
      })
      .catch((error) => {
        deferred.reject(error);
        // Network trouble: send them to the cart page rather than leave them stuck.
        window.location.href = (T.routes && T.routes.cart_url) || '/cart';
      })
      .finally(() => {
        if (button) button.disabled = false;
      });
  });
}

function init() {
  document.querySelectorAll('form#ev-form').forEach(wire);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The theme editor re-renders sections without a page load.
document.addEventListener('shopify:section:load', init);
